/**
 * Scales, ticks, mark paths and the responsive-width hook shared by every chart. Owner: slice web-charts (#1115).
 */
import * as React from 'react'

/** Measures the container so the SVG viewBox matches its pixel width (1 unit = 1 px, crisp hairlines). */
export function useWidth<T extends HTMLElement>(fallback = 640): [React.RefObject<T | null>, number] {
  const ref = React.useRef<T>(null)
  const [width, setWidth] = React.useState(fallback)
  React.useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const measure = () => {
      const w = Math.round(el.getBoundingClientRect().width)
      if (w > 0) setWidth(w)
    }
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])
  return [ref, width]
}

/** Rounded ticks covering [min, max], about `count` of them, always including 0 when the domain spans it. */
export function niceTicks(min: number, max: number, count = 4): number[] {
  let lo = Math.min(0, min)
  let hi = Math.max(0, max)
  if (lo === hi) hi = lo + 1
  const raw = (hi - lo) / count
  const mag = 10 ** Math.floor(Math.log10(raw))
  const norm = raw / mag
  const step = (norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 2.5 ? 2.5 : norm <= 5 ? 5 : 10) * mag
  lo = Math.floor(lo / step) * step
  hi = Math.ceil(hi / step) * step
  const ticks: number[] = []
  for (let v = lo; v <= hi + step / 2; v += step) ticks.push(Math.round(v / step) * step)
  return ticks
}

export function linear(d0: number, d1: number, r0: number, r1: number): (v: number) => number {
  const span = d1 - d0 || 1
  return (v) => r0 + ((v - d0) / span) * (r1 - r0)
}

/**
 * A vertical bar from `base` to `end` (SVG y), `r` rounded corners at the data end only, square at the baseline.
 * Works for negative values (end below base).
 */
export function vBarPath(x: number, w: number, base: number, end: number, r = 4): string {
  const h = Math.abs(base - end)
  const rr = Math.max(0, Math.min(r, w / 2, h))
  if (end <= base) {
    return `M${x},${base}V${end + rr}Q${x},${end} ${x + rr},${end}H${x + w - rr}Q${x + w},${end} ${x + w},${end + rr}V${base}Z`
  }
  return `M${x},${base}V${end - rr}Q${x},${end} ${x + rr},${end}H${x + w - rr}Q${x + w},${end} ${x + w},${end - rr}V${base}Z`
}

/** A horizontal bar from `base` to `end` (SVG x), rounded at the data end only. */
export function hBarPath(y: number, h: number, base: number, end: number, r = 4): string {
  const w = Math.abs(end - base)
  const rr = Math.max(0, Math.min(r, h / 2, w))
  if (end >= base) {
    return `M${base},${y}H${end - rr}Q${end},${y} ${end},${y + rr}V${y + h - rr}Q${end},${y + h} ${end - rr},${y + h}H${base}Z`
  }
  return `M${base},${y}H${end + rr}Q${end},${y} ${end},${y + rr}V${y + h - rr}Q${end},${y + h} ${end + rr},${y + h}H${base}Z`
}

/** Indices of x labels to draw so they never collide (about one per `minGap` px). */
export function labelEvery(count: number, plotWidth: number, minGap = 64): number {
  if (count <= 1) return 1
  return Math.max(1, Math.ceil(count / Math.max(1, Math.floor(plotWidth / minGap))))
}

/** Spread label y positions so no two are closer than `gap` px, keeping their order. */
export function spreadLabels(ys: number[], gap = 13, top = 0, bottom = Number.POSITIVE_INFINITY): number[] {
  const order = ys.map((y, i) => ({ y, i })).sort((a, b) => a.y - b.y)
  for (let k = 1; k < order.length; k++) {
    const prev = order[k - 1]
    const cur = order[k]
    if (prev && cur && cur.y - prev.y < gap) cur.y = prev.y + gap
  }
  const last = order[order.length - 1]
  if (last && last.y > bottom) {
    const shift = last.y - bottom
    for (const o of order) o.y = Math.max(top, o.y - shift)
  }
  const out = new Array<number>(ys.length)
  for (const o of order) out[o.i] = o.y
  return out
}
